import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Send } from 'lucide-react';
import { assistanceService } from '../../services/AssistanceService';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  reportStatus: 'idle' | 'sending' | 'sent' | 'failed';
  occurredAt: string | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    reportStatus: 'idle',
    occurredAt: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
      reportStatus: 'idle',
      occurredAt: new Date().toISOString(),
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private handleSendError = async () => {
    const { error, errorInfo } = this.state;
    if (!error || this.state.reportStatus === 'sending' || this.state.reportStatus === 'sent') return;

    this.setState({ reportStatus: 'sending' });
    try {
      const pageUrl = new URL(window.location.href);
      pageUrl.search = '';
      pageUrl.hash = '';
      await assistanceService.reportApplicationError({
        error_name: error.name || 'Error',
        error_message: error.message || String(error),
        error_stack: error.stack,
        component_stack: errorInfo?.componentStack || undefined,
        page_url: pageUrl.toString(),
        user_agent: navigator.userAgent,
        occurred_at: this.state.occurredAt || new Date().toISOString(),
        app_version: process.env.REACT_APP_VERSION,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        language: navigator.language,
      });
      this.setState({ reportStatus: 'sent' });
    } catch {
      this.setState({ reportStatus: 'failed' });
    }
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8 md:p-12">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-10 h-10 text-red-600" />
                </div>
              </div>
              
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Oops! Something went wrong
              </h1>
              
              <p className="text-gray-600 mb-8">
                We're sorry for the inconvenience. An unexpected error occurred while loading this page.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <button
                  onClick={this.handleReload}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  <RefreshCw className="w-5 h-5" />
                  Reload Page
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  <Home className="w-5 h-5" />
                  Go to Home
                </button>
              </div>

              <div className="flex flex-col items-center gap-2 mb-8">
                <button
                  onClick={this.handleSendError}
                  disabled={this.state.reportStatus === 'sending' || this.state.reportStatus === 'sent'}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60 transition-colors font-medium"
                >
                  <Send className="w-5 h-5" />
                  {this.state.reportStatus === 'sending'
                    ? 'Sending error…'
                    : this.state.reportStatus === 'sent'
                      ? 'Error sent to Cata support'
                      : 'Send error to Cata support'}
                </button>
                {this.state.reportStatus === 'sent' && (
                  <p role="status" className="text-sm text-green-700">Thank you. Cata support received the technical details.</p>
                )}
                {this.state.reportStatus === 'failed' && (
                  <p role="alert" className="text-sm text-red-700">The report could not be sent. Please reload and try again.</p>
                )}
              </div>

              {/* Error Details (collapsible) */}
              {this.state.error && (
                <details className="text-left bg-gray-50 rounded-lg p-4 mt-6">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                    Technical Details (for developers)
                  </summary>
                  <div className="mt-4 space-y-2">
                    <div className="text-xs">
                      <strong className="text-red-600">Error:</strong>
                      <pre className="mt-1 p-2 bg-white rounded border border-gray-200 overflow-auto text-xs">
                        {this.state.error.toString()}
                      </pre>
                    </div>
                    {this.state.errorInfo && (
                      <div className="text-xs">
                        <strong className="text-red-600">Component Stack:</strong>
                        <pre className="mt-1 p-2 bg-white rounded border border-gray-200 overflow-auto text-xs">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
